import React, { useEffect, useState, useCallback, useRef } from 'react';
import { clickable, useDrawerFocus } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const STATUS_CAT_COLOR = {
  done: 'var(--ok)', complete: 'var(--ok)',
  indeterminate: 'var(--accent)', 'in-progress': 'var(--accent)',
  new: 'var(--text-2)', todo: 'var(--text-2)',
};

function progressColor(pct) {
  if (pct >= 100) return 'var(--ok)';
  if (pct >= 50)  return 'var(--accent)';
  if (pct >= 20)  return 'var(--warn)';
  return 'var(--critical)';
}

function ProgressBar({ pct, height = 8 }) {
  return (
    <div style={{ background: 'var(--border)', borderRadius: 99, height, overflow: 'hidden', flex: 1 }}>
      <div style={{ height: '100%', width: `${pct}%`, background: progressColor(pct), borderRadius: 99, transition: 'width .4s ease' }} />
    </div>
  );
}

function StatusDot({ category }) {
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: STATUS_CAT_COLOR[category] || 'var(--text-2)', flexShrink: 0 }} />;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-BE', { day: '2-digit', month: 'short', year: 'numeric' , timeZone: 'Europe/Brussels' });
}

/* ── Sub-task row ── */
function SubtaskRow({ task, idx }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: '1px solid var(--border)', transition: 'background .1s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
      onMouseLeave={e => e.currentTarget.style.background = ''}>
      <span style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: "'IBM Plex Mono', monospace", width: 20, flexShrink: 0 }}>{idx + 1}</span>
      <StatusDot category={task.status_category} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <a href={task.url} target="_blank" rel="noopener noreferrer"
            style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none', background: 'var(--accent-light)', padding: '1px 6px', borderRadius: 4 }}>
            {task.key} ↗
          </a>
          <span className="badge badge-default" style={{ fontSize: 10 }}>{task.status}</span>
          {task.priority && <span style={{ fontSize: 10, color: 'var(--text-2)' }}>{task.priority}</span>}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.summary}</div>
      </div>
      <div style={{ width: 130, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--accent-light)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
            {task.assignee !== 'Unassigned' ? task.assignee.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() : '?'}
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.assignee}</span>
        </div>
      </div>
      <div style={{ width: 140, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <ProgressBar pct={task.progress_pct} height={6} />
        <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", color: progressColor(task.progress_pct), width: 32, textAlign: 'right', flexShrink: 0 }}>{task.progress_pct}%</span>
      </div>
      <div style={{ width: 80, textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 }}>{task.time_spent}</div>
        {task.time_estimate && task.time_estimate !== '—' && <div style={{ fontSize: 10, color: 'var(--text-2)' }}>/ {task.time_estimate}</div>}
      </div>
    </div>
  );
}

/* ── Subtask Drawer ── */
function SubtaskDrawer({ parentKey, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const drawerRef = useRef(null);
  useDrawerFocus(drawerRef);

  useEffect(() => {
    setLoading(true); setError(null);
    fetch(`${API}/api/jira/sales-orders/${parentKey}/subtasks`)
      .then(r => { if (!r.ok) throw new Error(`Server error ${r.status}`); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [parentKey]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const done  = data?.subtasks_done ?? 0;
  const total = data?.subtask_count ?? 0;
  const pct   = data?.progress_pct ?? 0;

  return (
    <>
      <div onClick={onClose} className="drawer-overlay" />
      <div className="drawer" style={{ width: 800, maxWidth: '96vw' }}
        role="dialog" aria-modal="true" aria-label={`Sub-tasks — ${parentKey}`} tabIndex={-1} ref={drawerRef}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              {data?.url
                ? <a href={data.url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 700, color: 'var(--accent)', textDecoration: 'none', background: 'var(--accent-light)', padding: '2px 8px', borderRadius: 5 }}>{parentKey} ↗</a>
                : <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-light)', padding: '2px 8px', borderRadius: 5 }}>{parentKey}</span>}
              {data?.status && <span className="badge badge-default">{data.status}</span>}
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{data?.summary || '…'}</div>
            {data && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ProgressBar pct={pct} height={10} />
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 700, color: progressColor(pct), flexShrink: 0 }}>{pct}%</span>
                <span style={{ fontSize: 12, color: 'var(--text-2)', flexShrink: 0 }}>{done}/{total} done</span>
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', flexShrink: 0 }}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {data && (
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            {[
              { label: 'Sub-tasks', value: total },
              { label: 'Done', value: done, color: 'var(--ok)' },
              { label: 'In progress', value: data.subtasks?.filter(s => ['indeterminate','in-progress'].includes(s.status_category)).length || 0, color: 'var(--accent)' },
              { label: 'Not started', value: data.subtasks?.filter(s => ['new','todo'].includes(s.status_category)).length || 0, color: 'var(--text-2)' },
              { label: 'Time logged', value: data.total_time_spent },
              { label: 'Time estimated', value: data.total_time_estimate || '—' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ flex: 1, padding: '12px 16px', borderRight: '1px solid var(--border)', textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: color || 'var(--text)' }}>{value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 20px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <span style={{ width: 20, flexShrink: 0 }} /><span style={{ width: 8, flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Sub-task</span>
          <span style={{ width: 130, fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em', flexShrink: 0 }}>Assignee</span>
          <span style={{ width: 140, fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em', flexShrink: 0 }}>Progress</span>
          <span style={{ width: 80, fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'right', flexShrink: 0 }}>Time</span>
        </div>

        <div className="drawer-body">
          {loading && <div className="loading">Loading sub-tasks from Jira…</div>}
          {error && <div style={{ padding: 32, textAlign: 'center' }}><div style={{ color: 'var(--critical)', fontWeight: 600, marginBottom: 8 }}>Could not load sub-tasks</div><div style={{ fontSize: 13, color: 'var(--text-2)' }}>{error}</div></div>}
          {!loading && !error && data?.subtasks?.length === 0 && <div className="empty"><p>No sub-tasks found.</p></div>}
          {!loading && !error && data?.subtasks?.map((task, idx) => <SubtaskRow key={task.key} task={task} idx={idx} />)}
        </div>

        <div className="drawer-footer">
          <span>Live data from Jira · {new Date().toLocaleTimeString('fr-BE', { timeZone: 'Europe/Brussels' })}</span>
          <span><StatusDot category="done" /> Done &nbsp;<StatusDot category="indeterminate" /> In progress &nbsp;<StatusDot category="new" /> Not started</span>
        </div>
      </div>
    </>
  );
}

/* ── Card View ── */
function CardView({ order, onClick }) {
  const pct = order.progress_pct;
  return (
    <div {...clickable(onClick)} aria-label={`${order.key}: ${order.summary}`}
      style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 20px', cursor: 'pointer', transition: 'all .15s', position: 'relative', overflow: 'hidden' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,99,235,.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--border)' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: progressColor(pct), transition: 'width .4s' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-light)', padding: '1px 7px', borderRadius: 4 }}>{order.key}</span>
            <span className="badge badge-default" style={{ fontSize: 10 }}>{order.status}</span>{order.issue_type === 'Project Plan' && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#ede9fe', color: '#7c3aed', border: '1px solid #c4b5fd' }}>Project</span>}
          </div>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', lineHeight: 1.4, wordBreak: 'break-word' }}>{order.summary}</div>
        </div>
        <div style={{ flexShrink: 0, position: 'relative', width: 48, height: 48 }}>
          <svg viewBox="0 0 52 52" style={{ transform: 'rotate(-90deg)', width: 48, height: 48 }}>
            <circle cx="26" cy="26" r="22" fill="none" stroke="var(--border)" strokeWidth="5" />
            <circle cx="26" cy="26" r="22" fill="none" stroke={progressColor(pct)} strokeWidth="5"
              strokeDasharray={`${2 * Math.PI * 22}`} strokeDashoffset={`${2 * Math.PI * 22 * (1 - pct / 100)}`}
              strokeLinecap="round" style={{ transition: 'stroke-dashoffset .4s' }} />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: progressColor(pct) }}>{pct}%</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{order.assignee}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: 'var(--text-2)' }}>{order.subtasks_done}/{order.subtask_count} tasks</span>
          {order.time_estimate_seconds > 0 && <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", color: 'var(--text-2)' }}>{order.time_estimate}</span>}
          {order.created && <span style={{ fontSize: 10, color: 'var(--text-2)' }}>{fmtDate(order.created)}</span>}
        </div>
      </div>
    </div>
  );
}

/* ── List View ── */
function ListView({ order, onClick }) {
  const pct = order.progress_pct;
  return (
    <div {...clickable(onClick)} aria-label={`${order.key}: ${order.summary}`}
      style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, transition: 'all .12s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-light)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface)'; }}>

      {/* Key */}
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-light)', padding: '2px 8px', borderRadius: 5, flexShrink: 0, minWidth: 90, textAlign: 'center' }}>{order.key}</span>

      {/* Summary */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{order.summary}</div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{order.assignee} · {fmtDate(order.created)}</div>
      </div>

      {/* Status */}
      <span className="badge badge-default" style={{ fontSize: 11, flexShrink: 0 }}>{order.status}</span>{order.issue_type === 'Project Plan' && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: '#ede9fe', color: '#7c3aed', border: '1px solid #c4b5fd', flexShrink: 0 }}>Project</span>}

      {/* Progress bar */}
      <div style={{ width: 120, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <ProgressBar pct={pct} height={6} />
        <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", color: progressColor(pct), width: 30, textAlign: 'right', flexShrink: 0 }}>{pct}%</span>
      </div>

      {/* Tasks */}
      <span style={{ fontSize: 11, color: 'var(--text-2)', flexShrink: 0, width: 60, textAlign: 'right' }}>{order.subtasks_done}/{order.subtask_count} tasks</span>
      {order.time_estimate_seconds > 0 && <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", color: 'var(--text-2)', flexShrink: 0, minWidth: 50, textAlign: 'right' }}>{order.time_estimate}</span>}

      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: 'var(--accent)', flexShrink: 0 }}><polyline points="9 18 15 12 9 6"/></svg>
    </div>
  );
}

/* ── Main Page ── */
export default function SalesOrderPage() {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [jiraOk, setJiraOk]   = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch]   = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const sortDir = sortBy.endsWith('-asc') ? 'asc' : 'desc';
  const toggleDir = () => { if (sortBy.endsWith('-desc')) setSortBy(sortBy.replace('-desc', '-asc')); else if (sortBy.endsWith('-asc')) setSortBy(sortBy.replace('-asc', '-desc')); else setSortBy(sortBy + '-asc'); };
  const [viewMode, setViewMode] = useState('list'); // 'card' | 'list'
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('Dimitri DE BACKER');
  const [filterProgress, setFilterProgress] = useState('all');

  useEffect(() => {
    fetch(`${API}/api/jira/status`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setJiraOk(d.configured); if (d.configured) loadOrders(); else { setLoading(false); setError(d.message); } })
      .catch(() => { setJiraOk(false); setLoading(false); setError('Cannot reach backend'); });
  }, []);

  const loadOrders = () => {
    setLoading(true);
    fetch(`${API}/api/jira/sales-orders`)
      .then(r => { if (!r.ok) throw new Error(`Error ${r.status}`); return r.json(); })
      .then(d => { setOrders(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  // Derived filter options
  const statuses  = ['all', ...new Set(orders.map(o => o.status).filter(Boolean))];
  const assignees = ['all', ...new Set(orders.map(o => o.assignee).filter(Boolean))];

  let filtered = orders.filter(o => {
    if (search && !o.key.toLowerCase().includes(search.toLowerCase()) && !o.summary.toLowerCase().includes(search.toLowerCase()) && !o.assignee.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus !== 'all' && o.status !== filterStatus) return false;
    if (filterAssignee !== 'all' && o.assignee !== filterAssignee) return false;
    if (filterProgress === 'not-started' && o.progress_pct > 0) return false;
    if (filterProgress === 'in-progress' && (o.progress_pct === 0 || o.progress_pct === 100)) return false;
    if (filterProgress === 'complete' && o.progress_pct < 100) return false;
    return true;
  });

  if (sortBy === 'date-desc')     filtered = [...filtered].sort((a, b) => new Date(b.created) - new Date(a.created));
  if (sortBy === 'date-asc')      filtered = [...filtered].sort((a, b) => new Date(a.created) - new Date(b.created));
  if (sortBy === 'progress')      filtered = [...filtered].sort((a, b) => a.progress_pct - b.progress_pct);
  if (sortBy === 'progress-desc') filtered = [...filtered].sort((a, b) => b.progress_pct - a.progress_pct);
  if (sortBy === 'name')          filtered = [...filtered].sort((a, b) => a.key.localeCompare(b.key));
  if (sortBy === 'tasks')         filtered = [...filtered].sort((a, b) => b.subtask_count - a.subtask_count);
  if (sortBy === 'updated')       filtered = [...filtered].sort((a, b) => new Date(b.updated) - new Date(a.updated));

  const avgProgress = orders.length ? Math.round(orders.reduce((s, o) => s + o.progress_pct, 0) / orders.length) : 0;
  const completed   = orders.filter(o => o.progress_pct === 100).length;
  const inProgress  = orders.filter(o => o.progress_pct > 0 && o.progress_pct < 100).length;
  const notStarted  = orders.filter(o => o.progress_pct === 0).length;
  const totalEstimateSecs = orders.reduce((s, o) => s + (o.time_estimate_seconds || 0), 0);
  const fmtTotalEst = (secs) => {
    if (!secs) return '—';
    const h = Math.round(secs / 3600);
    return h >= 8 ? `${(h / 8).toFixed(1)} d` : `${h} h`;
  };

  const selectStyle = { fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', outline: 'none' };

  if (!jiraOk && !loading) return (
    <div style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🔌</div>
      <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Jira not connected</div>
      <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{error}</div>
    </div>
  );

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        <div className="card" style={{ cursor: 'pointer' }} {...clickable(() => setFilterProgress('all'))} aria-label="Filter: all orders">
          <div className="card-title">Total Orders</div>
          <div className="card-value">{orders.length}</div>
          <div className="card-sub">Showing {filtered.length}</div>
        </div>
        <div className="card" style={{ cursor: 'pointer' }} {...clickable(() => setFilterProgress('not-started'))} aria-label="Filter: not started">
          <div className="card-title">Not Started</div>
          <div className="card-value" style={{ color: 'var(--critical)' }}>{notStarted}</div>
          <div className="card-sub">0% progress</div>
        </div>
        <div className="card" style={{ cursor: 'pointer' }} {...clickable(() => setFilterProgress('in-progress'))} aria-label="Filter: in progress">
          <div className="card-title">In Progress</div>
          <div className="card-value" style={{ color: 'var(--accent)' }}>{inProgress}</div>
          <div className="card-sub">Avg {avgProgress}%</div>
        </div>
        <div className="card" style={{ cursor: 'pointer' }} {...clickable(() => setFilterProgress('complete'))} aria-label="Filter: completed">
          <div className="card-title">Completed</div>
          <div className="card-value" style={{ color: 'var(--ok)' }}>{completed}</div>
          <div className="card-sub">All tasks done</div>
        </div>
        <div className="card">
          <div className="card-title">Total Estimated</div>
          <div className="card-value" style={{ fontSize: 22, fontFamily: "'IBM Plex Mono', monospace" }}>{fmtTotalEst(totalEstimateSecs)}</div>
          <div className="card-sub">{Math.round(totalEstimateSecs / 3600)} h across all plans</div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search */}
        <input type="text" placeholder="Search orders…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 160, fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 7, outline: 'none', background: 'var(--surface)' }} />

        {/* Filters */}
        <select style={selectStyle} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          {statuses.map(s => <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>)}
        </select>
        <select style={selectStyle} value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
          {assignees.map(a => <option key={a} value={a}>{a === 'all' ? 'All assignees' : a}</option>)}
        </select>
        <select style={selectStyle} value={filterProgress} onChange={e => setFilterProgress(e.target.value)}>
          <option value="all">All progress</option>
          <option value="not-started">Not started</option>
          <option value="in-progress">In progress</option>
          <option value="complete">Completed</option>
        </select>

        {/* Sort */}
        <select style={selectStyle} value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="date-desc">Date: Newest first</option>
          <option value="date-asc">Date: Oldest first</option>
          <option value="updated">Recently updated</option>
          <option value="progress">Progress: Low → High</option>
          <option value="progress-desc">Progress: High → Low</option>
          <option value="name">Key A→Z</option>
          <option value="tasks">Most sub-tasks</option>
        </select>
        <button onClick={toggleDir} title={sortDir === 'desc' ? 'Descending' : 'Ascending'}
          style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)', cursor: 'pointer', fontSize: 14, lineHeight: 1, color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>
          {sortDir === 'desc' ? '↓' : '↑'}
        </button>

        {/* View toggle */}
        <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 7, overflow: 'hidden', flexShrink: 0 }}>
          {[['list', <svg key="l" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>],
            ['card', <svg key="c" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>]
          ].map(([mode, icon]) => (
            <button key={mode} onClick={() => setViewMode(mode)} style={{ padding: '6px 10px', border: 'none', cursor: 'pointer', background: viewMode === mode ? 'var(--accent)' : 'var(--surface)', color: viewMode === mode ? '#fff' : 'var(--text-2)', display: 'flex', alignItems: 'center' }}>
              {icon}
            </button>
          ))}
        </div>

        {/* Refresh */}
        <button className="btn btn-secondary" onClick={loadOrders} style={{ flexShrink: 0 }}>
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          Refresh
        </button>

        {/* Active filters indicator */}
        {(filterStatus !== 'all' || filterAssignee !== 'all' || filterProgress !== 'all' || search) && (
          <button onClick={() => { setFilterStatus('all'); setFilterAssignee('all'); setFilterProgress('all'); setSearch(''); }}
            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 100, background: 'var(--warn-light)', color: 'var(--warn)', border: '1px solid #fde68a', cursor: 'pointer', fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 600 }}>
            ✕ Clear filters
          </button>
        )}
      </div>

      {/* Results count */}
      {filtered.length !== orders.length && (
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 12 }}>
          Showing <strong>{filtered.length}</strong> of <strong>{orders.length}</strong> orders
        </div>
      )}

      {/* Content */}
      {loading ? <div className="loading">Loading Plans & Orders from Jira…</div> :
        error ? <div className="empty"><p style={{ color: 'var(--critical)' }}>{error}</p></div> :
        filtered.length === 0 ? <div className="empty"><p>No orders match your filters.</p></div> : (
          viewMode === 'card'
            ? <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
                {filtered.map(o => <CardView key={o.key} order={o} onClick={() => setSelected(o.key)} />)}
              </div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {/* List header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '6px 16px', fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                  <span style={{ minWidth: 90 }}>Key</span>
                  <span style={{ flex: 1 }}>Summary</span>
                  <span style={{ width: 80, flexShrink: 0 }}>Status</span>
                  <span style={{ width: 156, flexShrink: 0 }}>Progress</span>
                  <span style={{ width: 60, textAlign: 'right', flexShrink: 0 }}>Tasks</span>
                  <span style={{ width: 14, flexShrink: 0 }} />
                </div>
                {filtered.map(o => <ListView key={o.key} order={o} onClick={() => setSelected(o.key)} />)}
              </div>
        )
      }

      {selected && <SubtaskDrawer parentKey={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
